import type { Transaction } from 'src/apis/capacity-management/types';
import type { ReshuffleActionForAllocation } from 'src/apis/reshuffle-service/types';

export const getAllocationIds = (transaction: Transaction): string[] => {
  switch (transaction.transactionType) {
    case 'AUCTION': {
      const ids = [];
      if (transaction.exit?.auctionAllocationId) {
        ids.push(transaction.exit.auctionAllocationId);
      }
      if (transaction.entry?.auctionAllocationId) {
        ids.push(transaction.entry.auctionAllocationId);
      }
      // exit and entry can share the same auctionAllocationId on interconnection points
      return [...new Set(ids)];
    }
    // No relevant for reshuffling
    case 'ASSIGNMENT_BUY':
    case 'TRANSFER_OF_USE_BUY':
    case 'TRANSFER_OF_USE_SELL':
    case 'ASSIGNMENT_SELL':
    case 'CONVERSION':
    case 'SURRENDER':
    case 'DIRECT_FCFS':
    case 'FCFS':
    case 'MULTI_YEAR_TRANSPORT_ALLOCATION':
    case 'RESHUFFLE':
      return [];
  }
};

const isAllocationReshuffleable = (
  reshuffleActionsForAllocation: ReshuffleActionForAllocation[],
  allocationId: string
): boolean => {
  const item = reshuffleActionsForAllocation.find(
    (i) => i.allocationId === allocationId
  );
  return Boolean(item?._links.createReshuffleRequest);
};

/**
 * Returns the first allocation id that is reshuffleable for the given transaction.
 * There can be two allocation ids (entry and exit) in the case of bundled network points,
 * so this returns the first one that is eligible for reshuffle.
 */
export const getReshuffleableTransactionAllocationId = (
  transaction: Transaction,
  reshuffleActionsForAllocation?: ReshuffleActionForAllocation[]
): string | undefined => {
  if (!reshuffleActionsForAllocation) return undefined;

  return getAllocationIds(transaction).find((allocationId) =>
    isAllocationReshuffleable(reshuffleActionsForAllocation, allocationId)
  );
};
